import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatBRL, formatDateBR } from "../../lib/api";
import { Receipt, ExternalLink, QrCode, Copy, AlertTriangle, Clock, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ClientFaturas() {
  const location = useLocation();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [justActivated, setJustActivated] = useState(false);
  const pollRef = useRef(null);

  const awaitingId = new URLSearchParams(location.search).get("awaiting");

  const load = () => api.get("/invoices").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  // Polling: when waiting for a specific invoice, refresh every 6s until paid
  useEffect(() => {
    if (!awaitingId) return;
    const tick = async () => {
      const { data } = await api.get("/invoices");
      setItems(data);
      const inv = data.find(i => i.id === awaitingId);
      if (inv && inv.status === "paid") {
        setJustActivated(true);
        if (pollRef.current) clearInterval(pollRef.current);
        toast.success("Pagamento confirmado! Seu pacote já está ativo.");
      }
    };
    pollRef.current = setInterval(tick, 6000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [awaitingId]);

  // Auto-expand the awaiting invoice
  useEffect(() => {
    if (awaitingId) setOpenId(awaitingId);
  }, [awaitingId]);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiado!"); };

  const awaitingInv = awaitingId ? items.find(i => i.id === awaitingId) : null;

  return (
    <div data-testid="client-faturas">
      <div className="mb-6 sm:mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Faturas</div>
        <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Minhas Faturas</h1>
      </div>

      {/* Awaiting payment banner com stepper */}
      {awaitingInv && (
        <div className="mb-6 rounded-lg overflow-hidden border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/10 to-white" data-testid="awaiting-banner">
          <div className="p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-7 h-7 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">1</div>
              <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">Pacote selecionado</span>
            </div>
            <div className="flex-1 h-px bg-gray-200 min-w-[24px]" />
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${awaitingInv.status === "paid" ? "bg-emerald-500 text-white" : "bg-[#D4AF37] text-[#14213D] animate-pulse"}`}>2</div>
              <span className={`text-sm font-semibold whitespace-nowrap ${awaitingInv.status === "paid" ? "text-emerald-700" : "text-[#7a6519]"}`}>
                {awaitingInv.status === "paid" ? "Pago" : "Aguardando pagamento…"}
              </span>
            </div>
            <div className="flex-1 h-px bg-gray-200 min-w-[24px]" />
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${justActivated || awaitingInv.status === "paid" ? "bg-emerald-500 text-white" : "border-2 border-gray-300 text-gray-400"}`}>3</div>
              <span className={`text-sm font-semibold whitespace-nowrap ${justActivated || awaitingInv.status === "paid" ? "text-emerald-700" : "text-gray-500"}`}>
                {justActivated || awaitingInv.status === "paid" ? "Pacote ativado ✓" : "Ativação automática"}
              </span>
            </div>
          </div>
          {(justActivated || awaitingInv.status === "paid") && (
            <div className="bg-emerald-50 border-t border-emerald-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm text-emerald-900 flex items-center gap-2"><CheckCircle2 size={16} /> Tudo pronto! Seu pacote já está disponível para uso.</div>
              <button onClick={() => nav("/portal/reservas")} className="bg-[#D4AF37] hover:bg-[#E8C657] text-[#14213D] px-4 py-2 rounded text-sm font-bold inline-flex items-center justify-center gap-1.5" data-testid="post-payment-reserve-btn">
                Fazer minha reserva agora <ArrowRight size={12} />
              </button>
            </div>
          )}
          {awaitingInv.status !== "paid" && (
            <div className="px-4 sm:px-5 pb-4 text-[11px] text-gray-500 flex items-center gap-1.5"><Clock size={11} /> Atualizamos automaticamente a cada 6s — você não precisa recarregar a página.</div>
          )}
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
            <Receipt size={32} className="mx-auto mb-3 text-gray-300" />
            Você ainda não tem faturas
          </div>
        ) : items.map((i) => {
          const link = i.asaas_invoice_url || i.asaas_bank_slip_url;
          const isOpen = openId === i.id;
          return (
            <div key={i.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`invoice-${i.id}`}>
              <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-md bg-[#14213D] text-[#D4AF37] flex items-center justify-center flex-shrink-0">
                    <Receipt size={18} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[#14213D] truncate">{i.description}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Vencimento {formatDateBR(i.due_date)} · {(i.method || "pix").toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:flex-shrink-0">
                  <div className="font-display text-xl text-[#14213D]">{formatBRL(i.amount)}</div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold ${
                    i.status === "paid" ? "bg-emerald-50 text-emerald-700" :
                    i.status === "overdue" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"
                  }`}>{i.status === "paid" ? "paga" : i.status === "overdue" ? "vencida" : "pendente"}</span>
                  {i.status !== "paid" && (
                    <>
                      {link && (
                        <a
                          href={link} target="_blank" rel="noreferrer"
                          className="bg-[#D4AF37] hover:bg-[#E8C657] text-[#14213D] px-3 py-1.5 rounded text-xs font-bold inline-flex items-center gap-1"
                          data-testid={`pay-link-${i.id}`}
                        >
                          <ExternalLink size={12} /> Pagar agora
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : i.id)}
                        className="border border-gray-300 text-[#14213D] hover:bg-gray-50 px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1"
                        data-testid={`pay-pix-${i.id}`}
                      >
                        <QrCode size={12} /> {isOpen ? "Fechar PIX" : "Pagar com PIX"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {isOpen && i.status !== "paid" && (
                <div className="border-t border-gray-100 bg-gray-50 p-4 sm:p-5" data-testid={`pix-area-${i.id}`}>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Pague com PIX</div>
                  {i.asaas_pix_qrcode && !i.asaas_pix_qrcode.startsWith("0002") && (
                    <img
                      src={i.asaas_pix_qrcode.startsWith("http") || i.asaas_pix_qrcode.startsWith("data:")
                        ? i.asaas_pix_qrcode
                        : `data:image/png;base64,${i.asaas_pix_qrcode}`}
                      alt="QR Code PIX"
                      className="w-44 h-44 bg-white p-2 rounded"
                    />
                  )}
                  {i.asaas_pix_copy_paste ? (
                    <div className="mt-3">
                      <div className="text-[11px] text-gray-500 mb-1">PIX copia-e-cola:</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-[10px] bg-white border border-gray-200 rounded px-2 py-2 break-all">{i.asaas_pix_copy_paste}</code>
                        <button
                          onClick={() => copy(i.asaas_pix_copy_paste)}
                          className="bg-[#14213D] text-white px-3 py-2 rounded text-xs inline-flex items-center gap-1"
                          data-testid={`copy-pix-${i.id}`}
                        ><Copy size={12} /> Copiar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-gray-500">
                      O QR Code aparece aqui assim que o gateway confirmar a geração.
                      {link && <> Enquanto isso, use o <a href={link} target="_blank" rel="noreferrer" className="text-[#14213D] underline">link de pagamento</a>.</>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {items.some(i => i.status === "overdue") && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900 flex items-start gap-2" data-testid="overdue-banner">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Você tem faturas vencidas. Para evitar bloqueio de reservas, regularize o pagamento o quanto antes.
          </div>
        </div>
      )}
    </div>
  );
}
